import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import React from "react";
import { TouchableOpacity, StyleSheet, View, type StyleProp, type ViewStyle } from "react-native";

import { DSText } from "../../design-system/components/DSText";
import { useAppTheme } from "../../design-system/ThemeProvider";
import { radius, shadows, spacing } from "../../design-system/tokens";

interface QuizNextButtonProps {
  onPress: () => void;
  disabled?: boolean;
  label: string;
  hint?: string;
  testID?: string;
  style?: StyleProp<ViewStyle>;
  variant?: "gradient" | "soft";
}

const QuizNextButton: React.FC<QuizNextButtonProps> = ({
  onPress,
  disabled = false,
  label,
  hint,
  testID = "quiz-next-button",
  style = {},
  variant = "gradient",
}) => {
  const { colors } = useAppTheme();
  const isSoft = variant === "soft";

  return (
    <View style={[styles.wrapper, style]}>
      {!!hint && (
        <DSText
          variant="caption"
          color={colors.onSurfaceVariant}
          align="center"
          style={styles.hint}
        >
          {hint}
        </DSText>
      )}
      <TouchableOpacity
        testID={testID}
        style={styles.touchable}
        onPress={onPress}
        activeOpacity={0.8}
        disabled={disabled}
      >
        {isSoft ? (
          <View
            style={[
              styles.button,
              styles.softButton,
              {
                backgroundColor: disabled ? colors.surfaceContainerHigh : colors.surfaceContainerLow,
                borderColor: disabled ? colors.outlineVariant : colors.primary,
              },
            ]}
          >
            <DSText
              variant="label"
              color={disabled ? colors.onSurfaceVariant : colors.primary}
              style={styles.text}
            >
              {label}
            </DSText>
            <Ionicons
              name="arrow-forward"
              size={18}
              color={disabled ? colors.onSurfaceVariant : colors.primary}
              style={styles.icon}
            />
          </View>
        ) : (
          <LinearGradient
            colors={disabled ? [colors.outlineVariant, colors.outlineVariant] : [colors.primary, colors.primaryDark]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            style={styles.button}
          >
            <DSText variant="label" color={colors.onPrimary} style={styles.text}>
              {label}
            </DSText>
            <Ionicons name="arrow-forward" size={18} color={colors.onPrimary} style={styles.icon} />
          </LinearGradient>
        )}
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    position: "absolute",
    bottom: 24,
    left: 0,
    right: 0,
    alignItems: "center",
    zIndex: 10,
    elevation: 10,
    paddingHorizontal: spacing.xl,
  },
  hint: {
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.lg,
  },
  touchable: {
    width: "100%",
    borderRadius: radius.full,
    overflow: "hidden",
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: radius.full,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.xl,
    width: '100%',
    justifyContent: 'center',
    ...shadows.md,
  },
  softButton: {
    borderWidth: 1.5,
  },
  text: {
    fontWeight: 'bold',
    fontSize: 17,
  },
  icon: {
    marginLeft: spacing.sm,
  },
});

export default QuizNextButton;
